import { alpha, lighten } from '@mui/material/styles';
import type { LandingTokens, PlansStyles, PlanVariant, Sx } from '../types';
import { BREAKPOINTS, down } from './breakpoints';

const perVariant = (base: Sx, signature: Sx = {}): Record<PlanVariant, Sx> => ({
  default: base,
  featured: base,
  signature: { ...base, ...signature },
});

export const plansStyles = (t: LandingTokens): PlansStyles => {
  const onDark = (opacity: number) => alpha(t.onDark, opacity);

  const billingButton: Sx = {
    fontFamily: t.sans,
    fontWeight: 600,
    fontSize: '13.5px',
    lineHeight: 1,
    border: 0,
    backgroundColor: 'transparent',
    color: t.muted,
    padding: '10px 18px',
    borderRadius: 999,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background .2s ease, color .2s ease',
  };

  const saveChip: Sx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '.04em',
    backgroundColor: lighten(t.brand, 0.82),
    color: t.brandInk,
    padding: '3px 8px',
    borderRadius: 999,
  };

  const card: Sx = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: t.paperHi,
    // Its own, not the page's: a block background can change what the section passes down.
    color: t.ink,
    border: `1px solid ${t.line}`,
    borderRadius: t.radius,
    padding: '28px 24px 24px',
    transition:
      'transform .25s cubic-bezier(.2,.7,.2,1), box-shadow .25s ease, border-color .25s ease',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: t.shadowLg,
      borderColor: 'rgba(16,24,32,.2)',
    },
  };

  const feature: Sx = {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
    color: t.inkSoft,
  };
  const featureIcon: Sx = {
    width: 15,
    height: 15,
    flex: 'none',
    marginTop: '2px',
    color: t.brand,
  };
  const tip: Sx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 15,
    height: 15,
    marginLeft: '6px',
    flex: 'none',
    verticalAlign: '-2px',
    border: '1px solid currentColor',
    borderRadius: '50%',
    fontFamily: t.sans,
    fontWeight: 700,
    fontSize: '9.5px',
    lineHeight: 1,
    fontStyle: 'normal',
    opacity: 0.5,
    cursor: 'help',
    '&:hover': { opacity: 1 },
  };

  return {
    section: {
      padding: '104px 0 96px',
      position: 'relative',
      [down(BREAKPOINTS.mobile)]: { padding: '72px 0 60px' },
    },
    billing: {
      display: 'flex',
      justifyContent: 'center',
      margin: '34px 0 44px',
      [down(BREAKPOINTS.mobile)]: { margin: '24px 0 30px' },
    },
    billingGroup: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px',
      border: `1px solid ${t.line}`,
      borderRadius: 999,
      backgroundColor: t.paperHi,
      boxShadow: t.shadowSm,
    },
    billingButton: {
      off: billingButton,
      on: { ...billingButton, backgroundColor: t.ink, color: t.paperHi },
    },
    saveChip: {
      off: saveChip,
      on: { ...saveChip, backgroundColor: lighten(t.brand, 0.35), color: t.ink },
    },
    // Wrapping flex rather than a grid, so a row short of plans, the last one included, centres.
    grid: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '16px',
      '& > *': { flex: '0 0 calc((100% - 48px) / 4)', minWidth: 0 },
      [down(BREAKPOINTS.plansTwoUp)]: { '& > *': { flexBasis: 'calc((100% - 16px) / 2)' } },
      [down(BREAKPOINTS.plansOneUp)]: { '& > *': { flexBasis: '100%' } },
    },
    flag: {
      position: 'absolute',
      top: -12,
      left: '50%',
      transform: 'translateX(-50%)',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '.12em',
      textTransform: 'uppercase',
      backgroundColor: t.brand,
      color: '#fff',
      padding: '6px 14px',
      borderRadius: 999,
      boxShadow: `0 8px 18px -8px ${alpha(t.brand, 0.8)}`,
      whiteSpace: 'nowrap',
    },
    foot: {
      textAlign: 'center',
      marginTop: '30px',
      fontSize: '13.5px',
      color: t.muted,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    footIcon: { width: 15, height: 15, flex: 'none', color: t.brand },
    card: {
      default: card,
      featured: {
        ...card,
        borderColor: alpha(t.brand, 0.55),
        boxShadow: `0 18px 44px -22px ${alpha(t.brand, 0.45)}`,
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: t.shadowLg,
          borderColor: t.brand,
        },
      },
      signature: {
        ...card,
        background: 'linear-gradient(165deg, #16212b, #101820 65%)',
        borderColor: 'rgba(255,255,255,.08)',
        color: t.onDark,
      },
    },
    tier: perVariant(
      {
        fontFamily: t.serif,
        fontSize: 23,
        fontWeight: 480,
        letterSpacing: '-.01em',
        marginBottom: '8px',
      },
      { color: t.gold },
    ),
    priceRow: { display: 'flex', alignItems: 'baseline', gap: '7px', margin: '18px 0 4px' },
    price: perVariant({
      fontFamily: t.serif,
      fontSize: 40,
      fontWeight: 430,
      letterSpacing: '-.02em',
    }),
    priceCallForPricing: {
      fontFamily: t.serif,
      fontSize: 27,
      fontStyle: 'italic',
      fontWeight: 420,
      letterSpacing: '-.01em',
    },
    per: perVariant({ fontSize: 13, color: t.muted }, { color: onDark(0.6) }),
    priceNote: perVariant(
      {
        fontSize: 12,
        color: t.muted,
        minHeight: 16,
        '& a': { color: 'inherit', textDecoration: 'underline' },
      },
      { color: onDark(0.6) },
    ),
    rule: perVariant(
      { border: 0, borderTop: `1px solid ${t.lineSoft}`, margin: '18px 0 16px' },
      { borderTopColor: 'rgba(255,255,255,.1)' },
    ),
    responsePill: perVariant(
      {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        alignSelf: 'flex-start',
        fontSize: '12.5px',
        fontWeight: 600,
        color: t.brandInk,
        backgroundColor: lighten(t.brand, 0.88),
        border: `1px solid ${alpha(t.brand, 0.28)}`,
        padding: '7px 12px',
        borderRadius: 999,
        marginBottom: '16px',
        '& svg': { width: 13, height: 13 },
      },
      {
        color: t.gold,
        backgroundColor: 'rgba(201,163,92,.12)',
        borderColor: 'rgba(201,163,92,.35)',
      },
    ),
    featureList: {
      listStyle: 'none',
      display: 'grid',
      gap: '10px',
      fontSize: '13.5px',
      margin: '0 0 22px',
      padding: 0,
    },
    feature: perVariant(feature, { color: onDark(0.82) }),
    featureIcon: perVariant(featureIcon, { color: t.gold }),
    featurePlus: perVariant({ ...feature, fontWeight: 600, color: t.ink }, { color: '#fff' }),
    featurePlusIcon: perVariant({ ...featureIcon, color: t.brandInk }, { color: t.gold }),
    tip: perVariant(tip),
    cta: { marginTop: 'auto', '& > *': { width: '100%' } },
    cardCta: { default: 'ghost', featured: 'brand', signature: 'light' },
  };
};
